import { decodePolyline } from "@/lib/utils"

export interface LatLng {
  lat: number
  lng: number
}

export interface Projection {
  distanceKm: number
  alongKm: number
  point: LatLng
}

export interface RideStopInput {
  name?: string
  price?: number
  order?: number
  location?: { coordinates?: number[] }
  lat?: number
  lng?: number
  /**
   * Centroide da cidade, em GeoJSON (`[lng, lat]`). Presente só em paradas de
   * cidade (`ibge:*`, e places refinados dentro dela). A `location` da parada é
   * o ponto ANCORADO NA ROTA e pode estar a dezenas de km do centroide, então
   * medir distância do passageiro contra ela daria um número sem sentido.
   */
  cityCenter?: { coordinates?: number[] } | null
  cityCode?: string | null
}

export interface RideLocationInput {
  location?: { coordinates?: number[] }
  lat?: number
  lng?: number
  price?: number
}

export interface RideRouteInput {
  polyline?: string
  sampledCoordinates?: Array<{ lat: number; lng: number }>
  // GeoJSON LineString. Também aceita o array solto `{lat,lng}[]` (que é o
  // que o denormalizador produz antes de virar GeoJSON) para as buscas internas.
  type?: string
  coordinates?:
    | { type?: string; coordinates: Array<[number, number]> }
    | Array<[number, number]>
    | Array<{ lat: number; lng: number }>
  totalKm?: number
  durationMin?: number
}

export interface RideForMatching {
  origin: RideLocationInput
  destination: RideLocationInput
  route?: RideRouteInput
  stops?: RideStopInput[]
  price?: number
  turboMode?: boolean
}

export interface RideSearch {
  originLat: number
  originLng: number
  destLat: number
  destLng: number
  maxRadiusKm: number
}

export interface MatchResult {
  matched: boolean
  reason: "ok" | "no-path" | "radius" | "order" | "off-route"
  originDistanceKm: number
  destDistanceKm: number
  originAlongKm: number
  destAlongKm: number
  boardPoint: LatLng
  alightPoint: LatLng
  segmentPrice: number
}

export function haversineKm(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371
  const dLat = ((lat2 - lat1) * Math.PI) / 180
  const dLng = ((lng2 - lng1) * Math.PI) / 180
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos((lat1 * Math.PI) / 180) *
      Math.cos((lat2 * Math.PI) / 180) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2)
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a))
  return R * c
}

function lerpLatLng(a: LatLng, b: LatLng, t: number): LatLng {
  return { lat: a.lat + t * (b.lat - a.lat), lng: a.lng + t * (b.lng - a.lng) }
}

function locationPoint(loc: RideLocationInput | RideStopInput): LatLng | null {
  if (loc.location?.coordinates && loc.location.coordinates.length >= 2) {
    return { lat: loc.location.coordinates[1], lng: loc.location.coordinates[0] }
  }
  if (typeof loc.lat === "number" && typeof loc.lng === "number") {
    return { lat: loc.lat, lng: loc.lng }
  }
  return null
}

function pointToSegmentProjection(
  p: LatLng,
  a: LatLng,
  b: LatLng
): { distanceKm: number; point: LatLng; fraction: number } {
  const dx = b.lat - a.lat
  const dy = b.lng - a.lng
  const lenSq = dx * dx + dy * dy
  if (lenSq === 0) {
    return { distanceKm: haversineKm(p.lat, p.lng, a.lat, a.lng), point: a, fraction: 0 }
  }
  let t = ((p.lat - a.lat) * dx + (p.lng - a.lng) * dy) / lenSq
  t = Math.max(0, Math.min(1, t))
  const point = { lat: a.lat + t * dx, lng: a.lng + t * dy }
  return { distanceKm: haversineKm(p.lat, p.lng, point.lat, point.lng), point, fraction: t }
}

export function pointToSegmentKm(p: LatLng, a: LatLng, b: LatLng): number {
  return pointToSegmentProjection(p, a, b).distanceKm
}

export function projectOnPathKm(point: LatLng, path: LatLng[]): Projection | null {
  if (path.length === 0) return null
  if (path.length === 1) {
    return {
      distanceKm: haversineKm(point.lat, point.lng, path[0].lat, path[0].lng),
      alongKm: 0,
      point: path[0],
    }
  }
  let best: Projection | null = null
  let along = 0
  for (let i = 0; i < path.length - 1; i++) {
    const a = path[i]
    const b = path[i + 1]
    const segLenKm = haversineKm(a.lat, a.lng, b.lat, b.lng)
    const proj = pointToSegmentProjection(point, a, b)
    const alongKm = along + proj.fraction * segLenKm
    if (!best || proj.distanceKm < best.distanceKm) {
      best = { distanceKm: proj.distanceKm, alongKm, point: proj.point }
    }
    along += segLenKm
  }
  return best
}

export function buildPathFromRide(ride: RideForMatching): LatLng[] {
  const originPt = locationPoint(ride.origin)
  const destPt = locationPoint(ride.destination)
  let path: LatLng[] = []
  if (ride.route?.polyline) {
    try {
      path = decodePolyline(ride.route.polyline)
    } catch {
      path = []
    }
  }
  if (path.length < 2) {
    const coords = ride.route?.coordinates
    if (Array.isArray(coords)) {
      if (coords.length >= 2 && typeof coords[0] === "object" && "lat" in coords[0]) {
        path = coords as Array<{ lat: number; lng: number }>
      } else if (coords.length >= 2 && Array.isArray(coords[0])) {
        path = (coords as Array<[number, number]>).map((c) => ({ lat: c[1], lng: c[0] }))
      }
    } else if (coords?.coordinates && coords.coordinates.length >= 2) {
      path = coords.coordinates.map((c: [number, number]) => ({ lat: c[1], lng: c[0] }))
    }
  }
  if (
    path.length < 2 &&
    ride.route?.sampledCoordinates &&
    ride.route.sampledCoordinates.length >= 2
  ) {
    path = ride.route.sampledCoordinates.map((p) => ({ lat: p.lat, lng: p.lng }))
  }
  if (path.length < 2 && originPt && destPt) {
    path = [originPt, destPt]
  }
  if (path.length >= 2) {
    if (originPt) path[0] = originPt
    if (destPt) path[path.length - 1] = destPt
  }
  return path
}

export function pathTotalKm(path: LatLng[], storedKm?: number): number {
  if (typeof storedKm === "number" && storedKm > 0) return storedKm
  let total = 0
  for (let i = 0; i < path.length - 1; i++) {
    total += haversineKm(path[i].lat, path[i].lng, path[i + 1].lat, path[i + 1].lng)
  }
  return total
}

/** Km acumulado de cada vértice do traçado (o primeiro vale 0). */
export function cumulativeKmAlong(path: LatLng[]): number[] {
  if (path.length === 0) return []
  const along: number[] = [0]
  for (let i = 0; i < path.length - 1; i++) {
    along.push(along[i] + haversineKm(path[i].lat, path[i].lng, path[i + 1].lat, path[i + 1].lng))
  }
  return along
}

/**
 * Ponto na rota a `targetAlongKm` do início. Interpolação pelo comprimento real
 * do segmento (a fração geométrica `t` do `projectOnPathKm` é projetada em
 * lat/lng, não em km, e aqui o alvo é um quilômetro).
 */
export function pointAtKmAlong(
  path: LatLng[],
  targetAlongKm: number
): { point: LatLng; alongKm: number } | null {
  if (path.length < 2) return null
  if (!Number.isFinite(targetAlongKm)) return null
  const along = cumulativeKmAlong(path)
  const total = along[along.length - 1]
  if (!(total > 0)) return null

  const clamped = Math.min(Math.max(targetAlongKm, 0), total)
  for (let i = 0; i < path.length - 1; i++) {
    if (clamped > along[i + 1]) continue
    const segKm = along[i + 1] - along[i]
    const t = segKm > 0 ? (clamped - along[i]) / segKm : 0
    return { point: lerpLatLng(path[i], path[i + 1], t), alongKm: clamped }
  }
  const last = path[path.length - 1]
  return { point: { lat: last.lat, lng: last.lng }, alongKm: total }
}

function nearestStopPoint(stops: RideStopInput[], point: LatLng, maxKm: number): LatLng | null {
  let best: LatLng | null = null
  let bestDist = maxKm
  for (const s of stops) {
    const p = locationPoint(s)
    if (!p) continue
    const d = haversineKm(point.lat, point.lng, p.lat, p.lng)
    if (d <= bestDist) {
      bestDist = d
      best = p
    }
  }
  return best
}

const MIN_SEGMENT_PRICE = 10
const TURBO_SURCHARGE = 1.2

export function computeSegmentPrice(
  ride: RideForMatching,
  boardAlongKm: number,
  alightAlongKm: number,
  path: LatLng[]
): number {
  const basePrice = ride.price || 0
  const totalKm = pathTotalKm(path, ride.route?.totalKm)
  const epsilon = 0.01
  const isFullRoute = boardAlongKm <= epsilon && alightAlongKm >= totalKm - epsilon
  if (isFullRoute) return Math.round(basePrice)

  const stops = (ride.stops || []).slice().sort((a, b) => (a.order ?? 0) - (b.order ?? 0))

  const boundaries: number[] = [0]
  const legPrices: number[] = []
  for (const s of stops) {
    const p = locationPoint(s)
    if (!p) continue
    const proj = projectOnPathKm(p, path)
    const along = proj ? proj.alongKm : null
    if (along == null || along <= boundaries[boundaries.length - 1] + 1e-6 || along >= totalKm - 1e-6) {
      continue
    }
    boundaries.push(along)
    legPrices.push(s.price || 0)
  }
  boundaries.push(totalKm)

  const stopSum = legPrices.reduce((acc, v) => acc + v, 0)
  const storedTail = ride.destination?.price
  const tail =
    typeof storedTail === "number" && storedTail > 0
      ? storedTail
      : Math.max(Math.round(basePrice) - stopSum, 0)
  legPrices.push(tail)

  let sum = 0
  for (let j = 0; j < boundaries.length - 1; j++) {
    const legStart = boundaries[j]
    const legEnd = boundaries[j + 1]
    const legKm = legEnd - legStart
    if (legKm <= 1e-6) continue
    const overlap = Math.min(alightAlongKm, legEnd) - Math.max(boardAlongKm, legStart)
    if (overlap <= 0) continue
    const frac = Math.min(overlap / legKm, 1)
    sum += (legPrices[j] || 0) * frac
  }

  if (sum <= 0) {
    if (totalKm > 0 && basePrice > 0) {
      sum = ((alightAlongKm - boardAlongKm) / totalKm) * basePrice
    } else {
      return Math.round(basePrice)
    }
  }

  if (ride.turboMode) sum = sum * TURBO_SURCHARGE

  const rounded = Math.round(sum)
  return Math.min(Math.max(rounded, MIN_SEGMENT_PRICE), Math.round(basePrice))
}

// Captura por cidade: um passageiro buscando "Foz do Iguaçu" pode estar a 15 km
// do centroide, mas a carona para na BR-101 a 3 km do centro. Sem isto, a
// busca por cidade morre no corte de 10 km da rota. Só vale para paradas que
// TÊM `cityCenter` (cidade) — place OSM refinado mantém o corte original.
export const CITY_CAPTURE_KM = 15

interface NearestStopResult extends Projection {
  /** A parada casa por `cityCenter`, não pelo ponto na rota. */
  cityCapture: boolean
}

function cityCenterPoint(s: RideStopInput): LatLng | null {
  const coords = s.cityCenter?.coordinates
  if (!Array.isArray(coords) || coords.length < 2) return null
  const lng = coords[0]
  const lat = coords[1]
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return { lat, lng }
}

function nearestStop(
  point: LatLng,
  stops: RideStopInput[] | undefined,
  path: LatLng[],
  radiusKm: number
): NearestStopResult | null {
  let best: NearestStopResult | null = null
  for (const s of stops || []) {
    const p = locationPoint(s)
    if (!p) continue

    // Âncora de medida: a parada ancorada na rota, ou o centroide da cidade.
    // O `alongKm` SEMPRE vem do ponto na rota (é ele que corta o trecho), mesmo
    // quando a distância foi medida a partir do centroide.
    const center = cityCenterPoint(s)
    const anchor = center || p
    const maxKm = center ? Math.max(radiusKm, CITY_CAPTURE_KM) : radiusKm
    const distanceKm = haversineKm(point.lat, point.lng, anchor.lat, anchor.lng)
    if (distanceKm > maxKm) continue
    if (best && distanceKm >= best.distanceKm) continue

    const proj = projectOnPathKm(p, path)
    if (!proj) continue
    const stopPoint: LatLng & { name?: string } = { ...p, name: s.name }
    best = { distanceKm, alongKm: proj.alongKm, point: stopPoint, cityCapture: !!center }
  }
  return best
}

export function matchRide(ride: RideForMatching, search: RideSearch): MatchResult {
  const originPt = locationPoint(ride.origin)
  const destPt = locationPoint(ride.destination)
  const path = buildPathFromRide(ride)

  const empty: MatchResult = {
    matched: false,
    reason: "no-path",
    originDistanceKm: Infinity,
    destDistanceKm: Infinity,
    originAlongKm: 0,
    destAlongKm: 0,
    boardPoint: originPt || { lat: search.originLat, lng: search.originLng },
    alightPoint: destPt || { lat: search.destLat, lng: search.destLng },
    segmentPrice: 0,
  }

  if (!originPt || !destPt || path.length < 2) return empty

  const searchOrigin: LatLng = { lat: search.originLat, lng: search.originLng }
  const searchDest: LatLng = { lat: search.destLat, lng: search.destLng }

  const MAX_OFF_ROUTE_KM = 10
  const snapRadiusKm = Math.min(search.maxRadiusKm, MAX_OFF_ROUTE_KM)
  const oSnap = nearestStop(searchOrigin, ride.stops, path, snapRadiusKm)
  const dSnap = nearestStop(searchDest, ride.stops, path, snapRadiusKm)
  const oProj = oSnap || (projectOnPathKm(searchOrigin, path) as Projection)
  const dProj = dSnap || (projectOnPathKm(searchDest, path) as Projection)

  // Corte "está na minha rota?". Uma parada de cidade capturada pelo centroide
  // pode estar a 15 km do centro e a 3 km do eixo — o passageiro pega na rota,
  // então o corte de 10 km não se aplica a ela. Parada comum ou projeção direta
  // seguem o corte antigo intacto.
  const offRouteKm = Math.min(search.maxRadiusKm, MAX_OFF_ROUTE_KM)
  const oOffRoute = oSnap?.cityCapture ? 0 : oProj.distanceKm
  const dOffRoute = dSnap?.cityCapture ? 0 : dProj.distanceKm
  if (oOffRoute > offRouteKm || dOffRoute > offRouteKm) {
    return {
      ...empty,
      reason: "off-route",
      originDistanceKm: oProj.distanceKm,
      destDistanceKm: dProj.distanceKm,
    }
  }

  // Raio do usuário. Para captura por cidade o que importa é a distância até o
  // CENTROIDE (é assim que a pessoa pensa: "estou a 12 km do centro"), que é
  // exatamente o `distanceKm` que `nearestStop` já calculou.
  if (oProj.distanceKm > search.maxRadiusKm || dProj.distanceKm > search.maxRadiusKm) {
    return {
      ...empty,
      reason: "radius",
      originDistanceKm: oProj.distanceKm,
      destDistanceKm: dProj.distanceKm,
    }
  }
  if (oProj.alongKm >= dProj.alongKm) {
    return {
      ...empty,
      reason: "order",
      originDistanceKm: oProj.distanceKm,
      destDistanceKm: dProj.distanceKm,
      originAlongKm: oProj.alongKm,
      destAlongKm: dProj.alongKm,
    }
  }

  // Return projected points directly - let caller find correct waypoints via indices
  const segmentPrice = computeSegmentPrice(ride, oProj.alongKm, dProj.alongKm, path)

  return {
    matched: true,
    reason: "ok",
    originDistanceKm: oProj.distanceKm,
    destDistanceKm: dProj.distanceKm,
    originAlongKm: oProj.alongKm,
    destAlongKm: dProj.alongKm,
    boardPoint: oProj.point,
    alightPoint: dProj.point,
    segmentPrice,
  }
}
